import { action } from "typesafe-actions";
import { IDataTableV2DateState } from "../../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../../initialStoreState";
import { AnyAction } from "redux";
import { IOperatingStatement } from "./OperatingStatements.types";
import { api } from "../../../api/api";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../messages/messagesActions";
import { getFileExtensionFromUrl } from "../../../helpers";
import { translateBackendError } from "../../../helpers/errorTranslator";

export const FETCH_OPERATING_STATEMENTS_LIST_PROGRESS =
  "FETCH_OPERATING_STATEMENTS_LIST_PROGRESS";
export const FETCH_OPERATING_STATEMENTS_LIST_SUCCESS =
  "FETCH_OPERATING_STATEMENTS_LIST_SUCCESS";
export const FETCH_OPERATING_STATEMENTS_LIST_FAILED =
  "FETCH_OPERATING_STATEMENTS_LIST_FAILED";

export const fetchOperatingStatementListProgress = () =>
  action(FETCH_OPERATING_STATEMENTS_LIST_PROGRESS);
export const fetchOperatingStatementListSuccess = (
  data: IOperatingStatement[],
  totalRecords: number
) => action(FETCH_OPERATING_STATEMENTS_LIST_SUCCESS, { data, totalRecords });
export const fetchOperatingStatementListFailed = () =>
  action(FETCH_OPERATING_STATEMENTS_LIST_FAILED);

/**
 * Optional branch-filter payload for the Operating Statement fetch thunk.
 *
 * Exactly one of `branch_codes` / `exclude_branch_codes` should be populated
 * at a time (the include/exclude toggle on the {@link BranchPicker} guarantees
 * this on the UI side). Each is serialized as REPEATED query params, e.g.
 * `&branch_codes=A&branch_codes=B`. An empty/omitted selection sends neither
 * param, which the backend treats as the whole company.
 */
export interface IOperatingStatementBranchFilter {
  branch_codes?: string[];
  exclude_branch_codes?: string[];
}

/**
 * Appends `branch_codes` / `exclude_branch_codes` as REPEATED query params to
 * an already-started query string (which must already contain at least one
 * param so the caller controls the leading `?`). Empty arrays are skipped.
 */
const appendBranchParams = (
  branchFilter: IOperatingStatementBranchFilter | undefined
): string => {
  if (!branchFilter) return "";
  const parts: string[] = [];
  (branchFilter.branch_codes ?? []).forEach((code) => {
    if (code) parts.push(`branch_codes=${encodeURIComponent(code)}`);
  });
  (branchFilter.exclude_branch_codes ?? []).forEach((code) => {
    if (code) parts.push(`exclude_branch_codes=${encodeURIComponent(code)}`);
  });
  return parts.length > 0 ? `&${parts.join("&")}` : "";
};

export const fetchOperatingStatementListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    status: string,
    date: IDataTableV2DateState["dates"],
    searchValue: IDatatableV2AdvancedSearchFilter,
    searchState: { [key: string]: any },
    branchFilter?: IOperatingStatementBranchFilter
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const { from_date, to_date, version } = searchState;
      dispatch(fetchOperatingStatementListProgress());
      let finalUrl = `/accounting/get-account-operating-statement-v2?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}&from_date=${from_date}&to_date=${to_date}`;
      
      if (searchValue.length > 0) {
        finalUrl = `/accounting/get-account-operating-statement-v2?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}&advanceFilter=${JSON.stringify(
          searchValue
        )}`;
      }
      if (status !== "-1") {
        finalUrl += "&status=" + status;
      }

      finalUrl += appendBranchParams(branchFilter);
      // if (from_date) {
      //   const filterObj = {
      //     column: ["date"],
      //     operator: "GREATER_THAN_EQUAL",
      //     value: from_date,
      //     logicalOperator: "AND",
      //   };

      //   const encodedFilter = encodeURIComponent(
      //     JSON.stringify(filterObj, null, 2)
      //   );
      //   finalUrl += `&advanceFilter=${encodedFilter}`;
      // }
      // if (to_date) {
      //   const filterObj = {
      //     column: ["date"],
      //     operator: "LESSER_THAN_EQUAL",
      //     value: to_date,
      //     logicalOperator: "AND",
      //   };

      //   const encodedFilter = encodeURIComponent(
      //     JSON.stringify(filterObj, null, 2)
      //   );
      //   finalUrl += `&advanceFilter=${encodedFilter}`;
      // }
      const res = await api.get(finalUrl);
      const data: IOperatingStatement[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchOperatingStatementListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchOperatingStatementListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        })
      );
    }
  };

export const fetchOperatingStatementListForExportAsync =
  (
    searchState: { [key: string]: any },
    onCallback: (isSuccess: boolean) => void,
    branchFilter?: IOperatingStatementBranchFilter
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const { from_date, to_date } = searchState;
      dispatch(saveLoaderProgress());

      const finalUrl =
        `/accounting/get-account-operating-statement-v2-pdf?from_date=${from_date}&to_date=${to_date}` +
        appendBranchParams(branchFilter);
     

       const res = await api.get(finalUrl, {
             responseType: "arraybuffer",
           });
     
           const blob = new Blob([res.data], {
             type: "application/" + getFileExtensionFromUrl("Balance_sheet.pdf"),
           });
           // const blob = new Blob([res.data], { type: "application/octet-stream" });
           const url = window.URL.createObjectURL(blob);
     
           // Create a link element and simulate a click to trigger the download
           const link = document.createElement("a");
           link.href = url;
          link.setAttribute("download", "Operating_Statement.pdf");// Set the desired file name and extension
           document.body.appendChild(link);
           link.click();
     
      onCallback(true);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: translateBackendError(err.response?.data),
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const CLEAR_OPERATING_STATEMENTS = "CLEAR_OPERATING_STATEMENTS";
export const clearOperatingStatements = () =>
  action(CLEAR_OPERATING_STATEMENTS);
